import {
  CHUNK_HEADER_SIZE,
  CHUNK_TYPE_DOCUMENT,
  CHUNK_TYPE_HEADER,
  SCBF_MAGIC,
  SCBF_VERSION,
  STATIC_HEADER_SIZE,
} from 'src/utils/collaboration/scbf/constants';

export interface SCBFDecodeResult {
  headers: Record<string, string>;
  documentPayload: Uint8Array;
}

const textDecoder = new TextDecoder('utf-8');

/**
 * Decode an SCBF binary buffer into its headers and document payload.
 *
 * @param input - The SCBF binary data as ArrayBuffer or Uint8Array
 * @returns The decoded headers and document payload
 * @throws If the buffer is invalid, has wrong magic/version, or is missing the document chunk
 */
export const decodeSCBF = (
  input: ArrayBuffer | Uint8Array
): SCBFDecodeResult => {
  const bytes = input instanceof Uint8Array ? input : new Uint8Array(input);
  const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);

  if (bytes.byteLength < STATIC_HEADER_SIZE) {
    throw new Error(
      `SCBF buffer too small: expected at least ${STATIC_HEADER_SIZE} bytes, got ${bytes.byteLength}`
    );
  }

  // Validate magic
  for (let i = 0; i < SCBF_MAGIC.length; i += 1) {
    if (bytes[i] !== SCBF_MAGIC[i]) {
      throw new Error('SCBF invalid magic: expected "STAR"');
    }
  }

  // Read version (uint32 big-endian at offset 8)
  const version = view.getUint32(8, false);
  if (version !== SCBF_VERSION) {
    throw new Error(
      `SCBF unsupported version: expected ${SCBF_VERSION}, got ${version}`
    );
  }

  // Read chunk count (uint32 big-endian at offset 12)
  const chunkCount = view.getUint32(12, false);

  const headers: Record<string, string> = {};
  let documentPayload: Uint8Array | null = null;
  let offset = STATIC_HEADER_SIZE;

  for (let i = 0; i < chunkCount; i += 1) {
    if (offset + CHUNK_HEADER_SIZE > bytes.byteLength) {
      throw new Error(`SCBF truncated: chunk ${i} header extends past buffer`);
    }

    const chunkType = bytes[offset];
    const chunkLength = view.getUint32(offset + 4, false);
    offset += CHUNK_HEADER_SIZE;

    if (offset + chunkLength > bytes.byteLength) {
      throw new Error(`SCBF truncated: chunk ${i} data extends past buffer`);
    }

    const chunkData = bytes.subarray(offset, offset + chunkLength);

    if (chunkType === CHUNK_TYPE_DOCUMENT) {
      documentPayload = chunkData;
    } else if (chunkType === CHUNK_TYPE_HEADER) {
      const headerView = new DataView(
        chunkData.buffer,
        chunkData.byteOffset,
        chunkData.byteLength
      );
      let headerOffset = 0;

      // nameLength (u32)
      const nameLength = headerView.getUint32(headerOffset, false);
      headerOffset += 4;

      // name (UTF-8)
      const name = textDecoder.decode(
        chunkData.subarray(headerOffset, headerOffset + nameLength)
      );
      headerOffset += nameLength;

      // valueLength (u32)
      const valueLength = headerView.getUint32(headerOffset, false);
      headerOffset += 4;

      // value (UTF-8)
      const value = textDecoder.decode(
        chunkData.subarray(headerOffset, headerOffset + valueLength)
      );

      // Last wins for duplicate header names
      headers[name] = value;
    } else {
      throw new Error(`SCBF unknown chunk type: 0x${chunkType.toString(16)}`);
    }

    offset += chunkLength;
  }

  if (!documentPayload) {
    throw new Error('SCBF missing required Document chunk');
  }

  return { headers, documentPayload };
};
